import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import  DeleteConfirmDialog  from './DeleteConfirmDialog';
import { Button, Card, Col, Container, Form, Modal, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import { PencilIcon, SearchIcon } from '@primer/octicons-react'
import { BiPlus, BiTrash } from "react-icons/bi";
import { axiosAuth_ASSET_MGMT_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import './AdminPages.css';
import { translateAPIError } from '../utils/apiUtils';
import { formatDate } from '../utils/timeUtils';


const AssetCollAdminDetailsView = (props) => {

  // Utilities
  const { t } = useTranslation(['common', 'custom']);
  const navigate = useNavigate();

  // Initialize with actual JSON obj.  If not, will get error.
  const [assetCollAttrs, setAssetCollAttrs] = useState({
    id: '',
    name: '',
    description: '',
    creatorUsername: '',
    timeCreated: '',
    updaterUsername: '',
    timeUpdated: ''
  });

  const [currentAssetCollAttrs, setCurrentAssetCollAttrs] = useState({});

  const [memberList,             setMemberList]             = useState([]);
  const [newMemberCandidateList, setNewMemberCandidateList] = useState();

  const [showMemberSelector,     setShowMemberSelector]     = useState(false);
  const [memberSearchTerm,       setMemberSearchTerm]       = useState('');
  const [memberAddList,          setMemberAddList]          = useState([]);
  const [errMsg_MemberSelect,    setErrMsg_MemberSelect]    = useState('');


  // UI Functions

  // Initialize state
  useEffect(() => {
    if (props) {
      setAssetCollAttrs(props.objDetails);
      setCurrentAssetCollAttrs(props.objDetails);

      fetchMembers(props.objDetails.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const fetchMembers = (id) => {

    if (!id) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_ASSET_MGMT_API.get("asset-colls/" + id + "/assets");

        resolve(res.data);

        if (res.status === 200) {
          setMemberList(res.data);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }


  // View vs Edit Modes
  const [editMode, setEditMode] = useState(false);

  const [errMsg,        setErrMsg]        = useState('');


  const handleEditAttrs = (e) => {
    e.preventDefault();
    setEditMode(true);
  }


  const onAttrsInputChange = (e) => {
    setAssetCollAttrs({ ...assetCollAttrs, [e.target.name]: e.target.value })
  }


  const handleEditCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setAssetCollAttrs(currentAssetCollAttrs);

    setErrMsg('');
    setEditMode(false);
  }

  const handleEditSubmit = (e) => {

    e.preventDefault();

    // Validate paraneters

    if (!assetCollAttrs.name || assetCollAttrs.name.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_name"));
      return;
    } else if (assetCollAttrs.name.length > 100) {
      setErrMsg(t("I18N_Name") + " - " + t("I18N_ERR_Max_len") + "100");
      return;
    } else if (!assetCollAttrs.description || assetCollAttrs.description.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_description"));
      return;
    } else if (assetCollAttrs.description.length > 250) {
      setErrMsg(t("I18N_ERR_Max_len_desc") + "250");
      return;
    }

    const reqBody = assetCollAttrs;

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_ASSET_MGMT_API.patch("asset-colls/" + assetCollAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {

          refreshAssetCollAttrs(assetCollAttrs.id);

          // Restore UI to viewing mode
          setEditMode(false);
        }
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });

  }


  const refreshAssetCollAttrs = (id) => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_ASSET_MGMT_API.get("asset-colls/" + id);

        resolve(res.data);

        if (res.status === 200) {

          setAssetCollAttrs(res.data[0]);
          setCurrentAssetCollAttrs(res.data[0]);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }


  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    navigate(-1);
  }

  function handleAddMember() {
    setShowMemberSelector(true);
    setMemberAddList([]);
  }

  const handleMemberSearch = (e) => {

    e.preventDefault(); // Prevents the default form submission behavior (page reload)

    if (memberSearchTerm === '') {
      setMemberSearchTerm('');
      setNewMemberCandidateList([]);
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_ASSET_MGMT_API.get("assets?searchTerms=" + memberSearchTerm + "&offset=0&limit=100");

        resolve(res.data);

        if (res.status === 200) {

          // Create non-member list
          let nonmemberList = [];

          for (let i=0; i<res.data.data.length; i++) {
            let foundMember = memberList.find(item => item.id === res.data.data[i].id);

            if (!foundMember || (foundMember == null)) {
              nonmemberList.push(res.data.data[i]);
            }
          }

          setNewMemberCandidateList(nonmemberList);
        }

        setErrMsg('');
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });

  }

  function handleMemberSelectorClose() {
    setShowMemberSelector(false);
  }


  function handleAddMembersSubmit() {
    let memberList = [];

    if (!memberAddList || memberAddList.length === 0) {
      setErrMsg_MemberSelect(t("I18N_ERR_Need_asset"));
      return;
    }

    for (let i=0; i<memberAddList.length; i++) {
      memberList.push(memberAddList[i]);
    }

    let reqBody = {
          assetCodes: memberList
        }

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_ASSET_MGMT_API.post("asset-colls/" + assetCollAttrs.id + "/assets", reqBody);

        resolve(res.data);

        if (res.status === 200) {
          // Refresh member list
          fetchMembers(assetCollAttrs.id);
          setShowMemberSelector(false);
        }
      } catch (error) {
        setErrMsg_MemberSelect(t(translateAPIError(error)));
      }
    });
  }


  function renderMember(asset, index) {
    return (
      <tr key={index} style={{fontWeight:'300'}}>
        <td>{asset.code}</td>
        <td>{asset.description}</td>
        <td>
          <OverlayTrigger overlay={<Tooltip id="tooltip-remove-member" style={{fontSize:'11px'}}>{t("I18N_TIP_Remove_member")}</Tooltip>}>
            <span style={{cursor:'pointer', color:'SteelBlue'}} onClick={() => handleRemoveMember(asset)}><BiTrash size={16} /></span>
          </OverlayTrigger>
        </td>
      </tr>
    )
  }


  const [errMsgDeleteConfirm,    setErrMsgDeleteConfirm]    = useState('');


  const [showMemberDeleteDialog, setShowMemberDeleteDialog] = useState(false);
  const [memberDeleteTarget,     setMemberDeleteTarget]     = useState(null);


  const onMemberSearchInputChange = (e) => {
    setMemberSearchTerm(e.target.value);
  }


  const handleMemberSelect = (e) => {

    if (e.target.checked) {
      // Add asset to the list
      memberAddList.push(e.target.id);
    } else {
      const index = memberAddList.indexOf(e.target.id);
      if (index > -1) {
        memberAddList.splice(index, 1);
      }
    }

console.log("memberAddList: ", memberAddList);
    setMemberAddList(memberAddList);
  }


  function renderCandidate(asset, index) {
    return (
      <tr key={index} style={{fontWeight:'300'}}>
        <td style={{width:"5%"}} ><Form.Check type="checkbox" id={asset.code} label="" onChange={handleMemberSelect} /></td>
        <td style={{width:"35%"}} >{asset.code}</td>
        <td style={{width:"60%"}} >{asset.description}</td>
      </tr>
    )
  }


  // Member delete confirmation dialog stuff

  function handleRemoveMember(asset) {
    setMemberDeleteTarget(asset);
    setShowMemberDeleteDialog(true);
  }

  function handleRemoveMemberCancel(asset) {
    setMemberDeleteTarget(null);
    setShowMemberDeleteDialog(false);
  }

  function handleRemoveMemberSubmit() {

    let reqBody = {
          data: {
             "assetCodes": []
          }
        };

    reqBody.data.assetCodes.push(memberDeleteTarget.code);

    return new Promise(async (resolve, reject) => {
      try {

        const res = await axiosAuth_ASSET_MGMT_API.delete("asset-colls/" + assetCollAttrs.id + "/assets/", reqBody);

        resolve(res.data);

        if (res.status === 200) {
          setErrMsgDeleteConfirm('');
          setShowMemberDeleteDialog(false);
          fetchMembers(assetCollAttrs.id);
        } else {
          // ##### NEED TO DECIDE WHAT TO DO #####
        }
      } catch (error) {
        setErrMsgDeleteConfirm(t(translateAPIError(error)));
      }
    });
  }



  // Main UI

  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_AssetColl_Details")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_AssetColl_info")}
            </span>
            {editMode ?
              null
              :
              <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrs}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>

          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editMode ?
            <Container>
              <form>
                <Row className={classes.admin_pages__field_label}>
                  <Col>{t("I18N_Name")}: *</Col>
                  <Col>{t("I18N_Description")}: *</Col>
                  <Col></Col>
                  <Col></Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="name" value={assetCollAttrs.name}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <textarea className={classes.admin_pages__field_editable} style={{width:'100%'}} name="description" value={assetCollAttrs.description}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col></Col>
                  <Col></Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>

                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {errMsg}
                    </span>
                  </div>
                </Row>
              </form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col>{t("I18N_Name")}:</Col>
                <Col>{t("I18N_Description")}:</Col>
                <Col></Col>
                <Col></Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{assetCollAttrs.name}</Col>
                <Col>
                  <textarea readOnly className={classes.admin_pages__field_editable} style={{width:'100%'}} name="description" value={assetCollAttrs.description} />
                </Col>
                <Col></Col>
                <Col></Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Created_by")}:</Col>
                <Col>{t("I18N_Time_created")}:</Col>
                <Col>{t("I18N_Updated_by")}:</Col>
                <Col>{t("I18N_Time_updated")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{overflowWrap:'anywhere'}}>{assetCollAttrs.creatorUsername}</Col>
                <Col>{formatDate(assetCollAttrs.timeCreated)}</Col>
                <Col style={{overflowWrap:'anywhere'}}>{assetCollAttrs.updaterUsername}</Col>
                <Col>{formatDate(assetCollAttrs.timeUpdated)}</Col>
              </Row>
            </Container>
          }
        </div>


        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'30px'}}>
          <div style={{display:'flex'}}>
            <span>
              {t("I18N_Member_assets")}
            </span>

            <span style={{marginLeft:'auto', marginRight:'20px'}}>
              <OverlayTrigger overlay={<Tooltip id="tooltip-new_role" style={{fontSize:'11px'}}>{t("I18N_TIP_Add_member_to_grp")}</Tooltip>}>
                <span onClick={handleAddMember} style={{ marginLeft: '0px', marginRight: '0px', marginTop: '3px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                  <BiPlus size={18} />
                </span>
              </OverlayTrigger>
            </span>
          </div>

          <hr className="solid" style={{ margin: '0 0 0 0' }}></hr>

          <div style={{overflow:'auto', fontSize:'11px', marginTop:'10px'}}>

            <Table hover responsive size="sm">
              <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
                <tr>
                  <th style={{width:'20%', fontWeight:'400'}}>{t("I18N_Code")}</th>
                  <th style={{width:'25%', fontWeight:'400'}}>{t("I18N_Description")}</th>
                  <th style={{width:'5%'}}></th>
                </tr>
              </thead>
              <tbody>
                {memberList.map(renderMember)}
              </tbody>
            </Table>
          </div>
        </div>


        <div className={classes.admin_pages__page_subtitle}>
          <Form.Group as={Row} className="mb-2 mt-3">
            <Col sm={{span: 2}}>
              <Button size="sm" variant="outline-secondary"
                style={{ fontSize: '12px', fontWeight: '300' }}
                onClick={handleClose}>
                {t("I18N_Close")}
              </Button>
            </Col>
          </Form.Group>
        </div>


        <Modal show={showMemberSelector} onHide={handleMemberSelectorClose} centered >
          <Modal.Body>
            <div className={classes.admin_pages__page_subtitle} style={{marginTop:'0px'}}>
              <span>
                {t("I18N_Asset_picker")}
              </span>

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

              <form onSubmit={handleMemberSearch}>
                <Row className={classes.admin_pages__field_label}>
                  <Col>{t("I18N_Asset_code")}: *</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <input className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="searchTerm"
                           placeholder={t("custom:I18N_Eg_search_asset")} onChange={(e) => onMemberSearchInputChange(e)} />
                  </Col>
                  <Col>
                    <span onClick={handleMemberSearch} style={{ marginLeft:'10px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                      <SearchIcon size={14} />
                    </span>
                  </Col>
                </Row>

                <hr className="solid" style={{ margin: '10px 0 10px 0' }}></hr>

                <div style={{height:'500px', overflow:'auto', fontSize:'11px', marginTop:'10px'}}>
                  <Table hover responsive size="sm">
                    <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
                      <tr>
                        <th style={{width:"5%"}}></th>
                        <th style={{width:"35%", fontWeight:'400'}}>{t("I18N_Code")}</th>
                        <th style={{width:"60%", fontWeight:'400'}}>{t("I18N_Description")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newMemberCandidateList ? newMemberCandidateList.map(renderCandidate) : null}
                    </tbody>
                  </Table>
                </div>

                <Row>
                  <div display='flex' style={{ marginTop: '15px' }}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleMemberSelectorClose}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleAddMembersSubmit}>
                      {t("I18N_Add_members")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px', color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                      {errMsg_MemberSelect}
                    </span>
                  </div>
                </Row>
              </form>
            </div>
          </Modal.Body>
        </Modal>


        <DeleteConfirmDialog
          title={t("I18N_Member_remove_confirm")}
          show={showMemberDeleteDialog}
          targetName={memberDeleteTarget ? memberDeleteTarget.code : ''}
          errMsgDeleteConfirm={errMsgDeleteConfirm}
          onCancel={handleRemoveMemberCancel}
          onSubmit={handleRemoveMemberSubmit}
        />

      </Card>
    </>
  );
}
 
export default AssetCollAdminDetailsView;
